import { useState, useRef, useCallback, useEffect } from 'react';
import { PageNode, PageMeta } from '@/types/sitemap';
import { X, ChevronRight, FileText, Paperclip, MessageCircle, Sparkles, Image, ClipboardCopy, Check, Upload } from 'lucide-react';
import { CommentThread } from './CommentThread';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ImageUploadModal } from '@/components/brand/image-upload/ImageUploadModal';

interface Props {
  page: PageNode;
  onUpdateMeta: (pageId: string, meta: Partial<PageMeta>) => void;
  onClose: () => void;
  initialTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  isGlobalDarkMode?: boolean;
}

const MIN_WIDTH = 320;
const MAX_WIDTH_PERCENT = 60;

type TabId = 'editor' | 'files' | 'comments';

export function PageEditorSidebar({ page, onUpdateMeta, onClose, initialTab = 'editor', onTabChange, isGlobalDarkMode = true }: Props) {
  const [width, setWidth] = useState(() => Math.round(window.innerWidth * 0.4));
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const { toast } = useToast();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const meta = page.meta || { metaTitle: '', metaDescription: '', jsonLd: '', featuredImage: '', featuredImageAlt: '' };

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX.current - ev.clientX;
      const maxWidth = Math.round(window.innerWidth * (MAX_WIDTH_PERCENT / 100));
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [width]);

  const handleGenerateJsonLd = () => {
    const schema = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": meta.metaTitle || page.name,
      "description": meta.metaDescription || '',
      ...(meta.featuredImage ? { "image": meta.featuredImage } : {}),
    }, null, 2);
    onUpdateMeta(page.id, { jsonLd: schema });
  };

  const handleGenerateMetaTitle = () => {
    const title = `${page.name} | Your Brand`;
    onUpdateMeta(page.id, { metaTitle: title.slice(0, 60) });
  };

  const handleGenerateMetaDescription = () => {
    const sectionNames = page.sections.map(s => s.name).join(', ');
    const desc = `Explore ${page.name} featuring ${sectionNames}. Discover more on our website.`;
    onUpdateMeta(page.id, { metaDescription: desc.slice(0, 160) });
  };

  const handleGenerateAltText = () => {
    onUpdateMeta(page.id, { featuredImageAlt: `Featured image for ${page.name} page` });
  };

  const copyToClipboard = useCallback((text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast({ title: 'Copied to clipboard' });
    setTimeout(() => setCopiedField(null), 2000);
  }, [toast]);

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'editor', label: 'Editor', icon: <FileText size={14} /> },
    { id: 'files', label: 'Files', icon: <Paperclip size={14} /> },
    { id: 'comments', label: 'Comments', icon: <MessageCircle size={14} /> },
  ];

  return (
    <div className="relative bg-card border-l border-border flex flex-col h-full shrink-0" style={{ width }}>
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-primary/20 active:bg-primary/30 transition-colors"
      />

      {/* Header */}
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0 text-sm">
          <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: page.color }} />
          <span className="font-semibold text-foreground truncate">{page.name}</span>
          <ChevronRight size={14} className="text-muted-foreground shrink-0" />
          <span className="text-muted-foreground truncate">Page Settings</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4 py-2 border-b border-border">
        <div className="relative flex items-center bg-muted/50 rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); onTabChange?.(tab.id); }}
              className={`relative z-10 flex items-center gap-1.5 flex-1 justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'text-secondary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="page-tab-indicator"
                  className="absolute inset-0 rounded-md bg-secondary"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                {tab.icon}
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'editor' && (
        <div className="flex-1 overflow-y-auto scrollbar-none px-5 py-4 flex flex-col gap-5">
          {/* Meta Title */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">Meta Title</label>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(meta.metaTitle, 'metaTitle')} className="h-6 gap-1 text-[10px] px-2" disabled={!meta.metaTitle}>
                  {copiedField === 'metaTitle' ? <Check size={10} /> : <ClipboardCopy size={10} />}
                  {copiedField === 'metaTitle' ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleGenerateMetaTitle} className="h-6 gap-1 text-[10px] px-2">
                  <Sparkles size={10} />
                  Generate
                </Button>
              </div>
            </div>
            <input
              type="text"
              value={meta.metaTitle}
              onChange={(e) => onUpdateMeta(page.id, { metaTitle: e.target.value })}
              placeholder="Page title for SEO (max 60 chars)"
              maxLength={60}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
            <span className="text-[10px] text-muted-foreground text-right">{meta.metaTitle.length}/60</span>
          </div>

          {/* Meta Description */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">Meta Description</label>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(meta.metaDescription, 'metaDescription')} className="h-6 gap-1 text-[10px] px-2" disabled={!meta.metaDescription}>
                  {copiedField === 'metaDescription' ? <Check size={10} /> : <ClipboardCopy size={10} />}
                  {copiedField === 'metaDescription' ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleGenerateMetaDescription} className="h-6 gap-1 text-[10px] px-2">
                  <Sparkles size={10} />
                  Generate
                </Button>
              </div>
            </div>
            <Textarea
              value={meta.metaDescription}
              onChange={(e) => onUpdateMeta(page.id, { metaDescription: e.target.value })}
              placeholder="Page description for SEO (max 160 chars)"
              maxLength={160}
              className="min-h-[72px] resize-none"
            />
            <span className="text-[10px] text-muted-foreground text-right">{meta.metaDescription.length}/160</span>
          </div>

          {/* Featured Image */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">Featured Image</label>
              <Button variant="ghost" size="sm" onClick={handleGenerateAltText} className="h-6 gap-1 text-[10px] px-2">
                <Sparkles size={10} />
                Generate Alt
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              {meta.featuredImage ? (
                <div className="relative rounded-lg border border-border overflow-hidden bg-muted">
                  <img src={meta.featuredImage} alt={meta.featuredImageAlt} className="w-full h-32 object-cover" />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center gap-2 opacity-0 hover:opacity-100">
                    <button
                      onClick={() => setShowImageModal(true)}
                      className="p-1.5 rounded-md bg-background/90 text-foreground hover:bg-background transition-colors text-xs font-medium flex items-center gap-1"
                    >
                      <Upload size={11} /> Change
                    </button>
                    <button
                      onClick={() => onUpdateMeta(page.id, { featuredImage: '', featuredImageAlt: '' })}
                      className="p-1.5 rounded-md bg-background/90 text-destructive hover:bg-background transition-colors"
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowImageModal(true)}
                  className="flex items-center justify-center h-24 rounded-lg border border-dashed border-border bg-muted/30 hover:bg-muted/60 hover:border-primary/50 transition-colors group"
                >
                  <div className="flex flex-col items-center gap-1.5 text-muted-foreground group-hover:text-foreground transition-colors">
                    <Upload size={18} />
                    <span className="text-[10px] font-medium">Upload or search Unsplash</span>
                  </div>
                </button>
              )}
              <input
                type="text"
                value={meta.featuredImageAlt}
                onChange={(e) => onUpdateMeta(page.id, { featuredImageAlt: e.target.value })}
                placeholder="Alt text for image"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            {/* Image Upload Modal */}
            <ImageUploadModal
              isOpen={showImageModal}
              onClose={() => setShowImageModal(false)}
              onImageSelect={(url) => {
                onUpdateMeta(page.id, { featuredImage: url });
                setShowImageModal(false);
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              title="Featured Image"
              showUnsplash={true}
              initialSearchQuery={page.name}
            />
          </div>

          {/* JSON-LD Schema */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">JSON-LD Schema</label>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(meta.jsonLd, 'jsonLd')} className="h-6 gap-1 text-[10px] px-2" disabled={!meta.jsonLd}>
                  {copiedField === 'jsonLd' ? <Check size={10} /> : <ClipboardCopy size={10} />}
                  {copiedField === 'jsonLd' ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateJsonLd}
                  className="h-6 gap-1 text-[10px] px-2"
                >
                  <Sparkles size={10} />
                  Generate
                </Button>
              </div>
            </div>
            <Textarea
              value={meta.jsonLd}
              onChange={(e) => onUpdateMeta(page.id, { jsonLd: e.target.value })}
              placeholder='{"@context": "https://schema.org", ...}'
              className="min-h-[120px] resize-none font-mono text-[11px]"
            />
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="flex-1 px-5 py-6 flex flex-col items-center justify-center text-center">
          <Paperclip size={32} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">No files yet</p>
          <p className="text-xs text-muted-foreground mb-4">Upload images, PDFs, or other attachments</p>
          <Button variant="outline" size="sm" className="gap-2">
            <Paperclip size={14} />
            Upload File
          </Button>
        </div>
      )}

      {activeTab === 'comments' && (
        <CommentThread key={`page-${page.id}`} threadId={`page-${page.id}`} />
      )}
    </div>
  );
}
